import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { CollaboratorAllocation, AllocationStatus } from '@/features/Collaborators/Models';
import { AllocationStatuses } from '@/features/Collaborators/Models';

export const collaboratorAllocationsQueryKeys = {
  all: ['collaborators', 'allocations'] as const,
  list: (collaboratorId: string) =>
    [...collaboratorAllocationsQueryKeys.all, collaboratorId] as const,
  detail: (collaboratorId: string, allocationId: string) =>
    [...collaboratorAllocationsQueryKeys.list(collaboratorId), 'allocation', allocationId] as const,
};

export interface UseCollaboratorAllocationsQueryResult {
  data: CollaboratorAllocation[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

interface SnapshotManager {
  unsub: (() => void) | null;
  listeners: number;
}

const snapshotManagers: Record<string, SnapshotManager> = {};

function getManager(collaboratorId: string): SnapshotManager {
  if (!snapshotManagers[collaboratorId]) {
    snapshotManagers[collaboratorId] = { unsub: null, listeners: 0 };
  }
  return snapshotManagers[collaboratorId];
}

function parseRate(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = value.replace(/\./g, '').replace(',', '.');
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function mapAllocation(doc: any): CollaboratorAllocation {
  const raw = doc.data() as Record<string, any>;
  return {
    id: doc.id,
    allocationType: raw.allocationType,
    durationType: raw.durationType,
    bucketType: raw.bucketType,
    bucket: raw.bucket ?? 0,
    startDate: raw.startDate,
    endDate: raw.endDate ?? null,
    status: (raw.status ?? AllocationStatuses.Active) as AllocationStatus,
    clientId: raw.clientId ?? null,
    clientName: raw.clientName ?? null,
    clientLogo: raw.clientLogo ?? null,
    clientResponsible: raw.clientResponsible ?? null,
    departmentId: raw.departmentId ?? null,
    departmentName: raw.departmentName ?? null,
    projectId: raw.projectId ?? null,
    projectName: raw.projectName ?? null,
    capacityId: raw.capacityId ?? null,
    capacityLabel: raw.capacityLabel ?? null,
    capacityTypeId: raw.capacityTypeId ?? null,
    capacityTypeName: raw.capacityTypeName ?? null,
    capacityTechnologyId: raw.capacityTechnologyId ?? null,
    capacityTechnologyName: raw.capacityTechnologyName ?? null,
    capacitySeniorityId: raw.capacitySeniorityId ?? null,
    capacitySeniorityName: raw.capacitySeniorityName ?? null,
    rate: parseRate(raw.rate),
    responsibleId: raw.responsibleId ?? null,
    responsibleName: raw.responsibleName ?? null,
    responsibleAvatar: raw.responsibleAvatar ?? null,
    createdById: raw.createdById ?? '',
    createdByName: raw.createdByName ?? '',
    createdByAvatar: raw.createdByAvatar ?? null,
    updatedById: raw.updatedById ?? null,
    updatedByName: raw.updatedByName ?? null,
    updatedByAvatar: raw.updatedByAvatar ?? null,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    cancelReason: raw.cancelReason ?? null,
    noticeReason: raw.noticeReason ?? null,
    terminationAppliedAt: raw.terminationAppliedAt ?? null,
    canceledAt: raw.canceledAt ?? null,
    canceledById: raw.canceledById ?? null,
    canceledByName: raw.canceledByName ?? null,
    canceledByAvatar: raw.canceledByAvatar ?? null,
  } satisfies CollaboratorAllocation;
}

export function useCollaboratorAllocationsQuery(
  collaboratorId?: string | null
): UseCollaboratorAllocationsQueryResult {
  const queryClient = useQueryClient();

  const queryKey = useMemo(() => {
    return collaboratorId
      ? (['collaborators', 'allocations', collaboratorId] as const)
      : collaboratorAllocationsQueryKeys.all;
  }, [collaboratorId]);

  const queryResult = useQuery<CollaboratorAllocation[]>({
    enabled: Boolean(collaboratorId),
    queryKey,
    initialData: () =>
      collaboratorId
        ? (queryClient.getQueryData(queryKey) as CollaboratorAllocation[] | undefined)
        : [],
    queryFn: async () => {
      if (!collaboratorId) {
        return [];
      }

      const allocationsRef = collection(
        db,
        'collaborators_private',
        collaboratorId,
        'allocations'
      );
      const snap = await getDocs(query(allocationsRef, orderBy('startDate', 'desc')));
      return snap.docs.map(mapAllocation);
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (!collaboratorId) {
      return;
    }

    // Mark cache as fetching to avoid flicker before first snapshot
    queryClient.setQueryData(queryKey, (prev) => prev ?? []);

    const manager = getManager(collaboratorId);
    manager.listeners += 1;

    if (!manager.unsub) {
      const allocationsRef = collection(
        db,
        'collaborators_private',
        collaboratorId,
        'allocations'
      );
      const allocationsQuery = query(allocationsRef, orderBy('startDate', 'desc'));

      manager.unsub = onSnapshot(allocationsQuery, (snapshot) => {
        const allocations = snapshot.docs.map(mapAllocation);
        queryClient.setQueryData(queryKey, allocations);
      });
    }

    return () => {
      manager.listeners -= 1;
      if (manager.listeners === 0 && manager.unsub) {
        manager.unsub();
        manager.unsub = null;
      }
    };
  }, [collaboratorId, queryClient, queryKey]);

  return {
    data: queryResult.data ?? [],
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: (queryResult.error as Error) ?? null,
  };
}

export default useCollaboratorAllocationsQuery;
